/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import Layout from "../../layout";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetEvidenceRequest,
  useSubmitForReviewEvidenceRequest,
} from "../../hooks/evidencerequests";
import EvidenceRequestChatInterface from "../../components/chat";
import { EvidenceRequest } from "../../types/evidencerequests";
import VerifierSelector from "./components/verifier";
import UserSelector from "./components/users";
import EngagementTable from "../../components/tables/engagements";
import { TabPanel, a11yProps } from "../../helpers/functions";
import ControlTable from "../../components/tables/controls";
import { getUserInfo } from "../../helpers/user";
import RelatedUserTable from "../../components/tables/users/related";
import EvidenceFileManager from "./fileManager";
import BlockingRelationships from "./components/blocks";

const UserEditEvidenceRequestPage = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <div>No ID provided</div>;
  }
  const [editedEvidenceRequest, setEditedEvidenceRequest] =
    useState<EvidenceRequest>();
  const navigator = useNavigate();
  const userInfo = getUserInfo();

  const isOrgAdmin = userInfo.role === "org_admin";

  const {
    data: evidenceRequestData,
    isLoading: evidenceRequestLoading,
    error: evidenceRequestError,
    refetch,
  } = useGetEvidenceRequest({ id });

  const [showSubmitForReviewButton, setShowSubmitForReviewButton] =
    useState(false);
  const [blockingRequestsExist, setBlockingRequestsExist] = useState(false);
  const [blockingRequestsText, setBlockingRequestsText] = useState<string>("");

  useEffect(() => {
    setBlockingRequestsExist(false);
  }, [id]);

  useEffect(() => {
    if (!editedEvidenceRequest) {
      return;
    }

    const blockingRequests =
      editedEvidenceRequest &&
      editedEvidenceRequest.blocked_by.filter(
        (request) => request.status !== "Accepted"
      );

    if (blockingRequests && blockingRequests.length > 0) {
      const requestList = blockingRequests
        .map((request) => `- ${request.title}`)
        .join("\n");

      setBlockingRequestsText(requestList);
      setBlockingRequestsExist(true);
    }
    setShowSubmitForReviewButton(true);
  }, [editedEvidenceRequest]);

  const submitEvidenceRequestForReview = useSubmitForReviewEvidenceRequest(id);

  const submitForReview = async () => {

    if (blockingRequestsExist) {
      alert(
        `The following evidence requests need to be accepted before this one can be submitted:\n\n${blockingRequestsText}`
      );
      return;
    }

    let note = "";
    const alertnote = window.prompt(
      "(Optional) Any notes to include with the submission?"
    );
    if (alertnote != null) {
      note = alertnote;
    }

    const confirmSubmit = window.confirm(
      "Are you sure you want to submit the Evidence Request for review?"
    );

    if (!editedEvidenceRequest) return;
    if (!confirmSubmit) return;

    const result = await submitEvidenceRequestForReview(note);
    if (result) {
      refetch();
    }
  };

  useEffect(() => {
    if (evidenceRequestData) {
      if (evidenceRequestData.evidencerequests[0]) {
        setEditedEvidenceRequest(evidenceRequestData.evidencerequests[0]);
      } else {
        window.alert("Evidence request not found!");
        navigator("/evidencerequests");
      }
    }
  }, [evidenceRequestData, evidenceRequestLoading]);

  const formattedDate = editedEvidenceRequest?.due_date
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(editedEvidenceRequest.due_date))
    : "";

  const [activeTab, setActiveTab] = useState(0);

  const handleTabClick = (tabId: number) => {
    setActiveTab(tabId);
  };

  const evidenceRequestInfoCard = (
    <>
      <Box
        sx={{
          m: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">{editedEvidenceRequest?.title}</Typography>

        {showSubmitForReviewButton && (
          <Box sx={{ display: "flex", gap: 2, pb: 2 }}>
            {(editedEvidenceRequest?.status === "In Progress" ||
              editedEvidenceRequest?.status === "Followup Required" ||
              editedEvidenceRequest?.status === "Overdue") && (
              <Button
                variant="contained"
                color="primary"
                sx={{ textTransform: "none" }}
                disabled={blockingRequestsExist}
                onClick={submitForReview}
              >
                {blockingRequestsExist
                  ? "Submission Blocked by Other Requests"
                  : "Submit For Review"}
              </Button>
            )}
          </Box>
        )}
      </Box>
      {editedEvidenceRequest && (
        <>
          <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}></Box>

          <Box>
            <Card sx={{ width: "100%", mb: 2 }}>
              <CardContent>
                <Chip
                  label={`Status: ${editedEvidenceRequest?.status}`}
                  sx={{ p: 0.5 }}
                  size="small"
                  variant="outlined"
                />

                {editedEvidenceRequest.description && (
                  <Typography
                    component="div"
                    variant="body1"
                    sx={{
                      lineHeight: "1em",
                      borderColor: "divider",
                      overflow: "wrap",
                    }}
                  >
                    <ReactMarkdown>
                      {editedEvidenceRequest.description}
                    </ReactMarkdown>
                  </Typography>
                )}
                {formattedDate && (
                  <Box>
                    <Typography
                      component="div"
                      variant="body1"
                      sx={{
                        lineHeight: "1em",
                        borderColor: "divider",
                        overflow: "wrap",
                      }}
                    >
                      Due Date: {formattedDate}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </>
  );

  const evidenceRequestInfo = (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => handleTabClick(newValue)}
            aria-label="Switch tabs"
          >
            <Tab label="Controls" {...a11yProps(0)} />
            <Tab label="Engagements" {...a11yProps(1)} />
            {isOrgAdmin && <Tab label="Users" {...a11yProps(2)} />}
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {editedEvidenceRequest && (
            <>
              <ControlTable
                parentObjectUrlKey="evidencerequests"
                loadPartial={true}
                parentObject={editedEvidenceRequest}
                relatedChildFieldName="controls"
                showRowCheckBoxes={false}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {editedEvidenceRequest && (
            <>
              <EngagementTable
                parentObjectUrlKey="evidencerequests"
                loadPartial={true}
                parentObject={editedEvidenceRequest}
                relatedChildFieldName="engagements"
                showRowCheckBoxes={false}
              />
            </>
          )}
        </TabPanel>
        {isOrgAdmin && (
          <TabPanel value={activeTab} index={2}>
            {editedEvidenceRequest && (
              <>
                <RelatedUserTable
                  parentObjectUrlKey="evidencerequests"
                  loadPartial={false}
                  parentObject={editedEvidenceRequest}
                  relatedChildFieldName="users"
                />
              </>
            )}
          </TabPanel>
        )}
      </Box>
    </>
  );
  return (
    <Layout
      pageTitle="Evidence Request"
      parentUrl="/evidencerequests"
      parentPageName="All Evidence Requests"
      engagements={editedEvidenceRequest?.engagements}
    >
      <Box sx={{ p: 3, color: "black" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              margin: 2,
              mb: 0,
            }}
          >
            {evidenceRequestInfoCard}
          </Box>

          <Grid container spacing={2} padding={2} paddingY={0}>
            <Grid item sm={12} md={5}>
              <Paper sx={{ height: "100%", padding: 1 }}>
                <Typography variant="h5" sx={{ p: 2, pb: 0 }}>
                  Conversation
                </Typography>{" "}
                {editedEvidenceRequest && (
                  <EvidenceRequestChatInterface
                    evidenceRequest={editedEvidenceRequest}
                    conversationId={
                      evidenceRequestData?.evidencerequests[0]
                        ?.message_history ?? ""
                    }
                  />
                )}
              </Paper>
            </Grid>
            <Grid item sm={12} md={7}>
              {editedEvidenceRequest && (
                <EvidenceFileManager evidenceRequest={editedEvidenceRequest} />
              )}
            </Grid>
          </Grid>

          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              margin: 2,
              marginTop: 0,
              borderRadius: 3,
            }}
          >
            {editedEvidenceRequest && (
              <Box sx={{ pt: 4, display: "flex", width: "100%", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <BlockingRelationships
                    evidenceRequest={editedEvidenceRequest}
                  />
                </Box>
              </Box>
            )}
            <Box sx={{ pt: 4, display: "flex", gap: 1 }}>
              {editedEvidenceRequest &&
                editedEvidenceRequest.users.length != 0 &&
                setEditedEvidenceRequest && (
                  <Box sx={{ flex: 1 }}>
                    <UserSelector
                      editMode={false}
                      editedEvidenceRequest={editedEvidenceRequest}
                      setEditedEvidenceRequest={setEditedEvidenceRequest}
                    />
                  </Box>
                )}
              {editedEvidenceRequest &&
                editedEvidenceRequest.verifiers.length != 0 &&
                setEditedEvidenceRequest && (
                  <Box sx={{ flex: 1 }}>
                    <VerifierSelector
                      editMode={false}
                      editedEvidenceRequest={editedEvidenceRequest}
                      setEditedEvidenceRequest={setEditedEvidenceRequest}
                    />
                  </Box>
                )}
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              color: "black",
              border: 2,
              borderColor: "divider",
              margin: 2,
              borderRadius: 3,
            }}
          >
            {evidenceRequestInfo}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default UserEditEvidenceRequestPage;
